/* "This is a gift" in the basket and drawer (the thread, 30 Oct 2026).
   Ported from live's cart.js (rnCreateGiftStore + RnCartGift) so the remake keeps live's gift orders exactly:
   cart attributes Gift, Gift message, Gift wrap. One store is shared by the drawer and the basket page, so the
   stock section redraws (which replace the drawer's HTML) never lose what the buyer typed, and nothing is kept
   in local storage. Checkout always carries on: if a save is still running it waits for it, then submits. */
(() => {
  if (customElements.get('rn-cart-gift')) return;

  const createStore = (send, notify) => {
    let attributes;
    let revision = 0;
    let savedRevision = 0;
    let pending = null;
    let error = false;
    let saved = false;
    // An unticked box saves empty values, so ordinary orders carry no gift details at all.
    const normalise = (gift, message, wrap) => ({
      Gift: gift ? 'Yes' : '',
      'Gift message': gift ? String(message).slice(0, 200) : '',
      'Gift wrap': gift && wrap ? 'Yes' : ''
    });
    const flush = () => {
      if (pending) return pending;
      if (savedRevision === revision && !error) return Promise.resolve(true);
      pending = (async () => {
        error = false;
        notify();
        try {
          while (savedRevision !== revision) {
            const savingRevision = revision;
            await send({ ...attributes });
            savedRevision = savingRevision;
          }
          saved = true;
          return true;
        } catch (failure) {
          error = true;
          return false;
        } finally {
          pending = null;
          notify();
        }
      })();
      return pending;
    };
    const draft = (gift, message, wrap) => {
      attributes = normalise(gift, message, wrap);
      revision += 1;
      saved = false;
      notify();
    };
    return {
      initialise(gift, message, wrap) {
        if (!attributes) attributes = normalise(gift, message, wrap);
      },
      draft,
      update(gift, message, wrap) {
        draft(gift, message, wrap);
        return flush();
      },
      flush,
      get state() {
        return { attributes, saving: savedRevision !== revision && !error, error, saved };
      }
    };
  };

  const store = createStore(
    async (attributes) => {
      const response = await fetch(window.FoxTheme?.routes?.cart_update_url || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        ...(typeof AbortSignal.timeout === 'function' ? { signal: AbortSignal.timeout(10000) } : {}),
        // Lets the save finish if the buyer goes straight on to checkout.
        keepalive: true,
        body: JSON.stringify({ attributes })
      });
      if (!response.ok) throw new Error('Gift details not saved');
      const cart = await response.json().catch(() => null);
      if (cart?.errors) throw new Error('Gift details not saved');
    },
    () => document.querySelectorAll('rn-cart-gift').forEach((element) => element.renderState())
  );

  class RnCartGift extends HTMLElement {
    connectedCallback() {
      this.checkbox = this.querySelector('[data-gift-checkbox]');
      this.giftValue = this.querySelector('[data-gift-value]');
      this.message = this.querySelector('textarea');
      this.field = this.querySelector('[data-gift-field]');
      this.status = this.querySelector('[role="status"]');
      this.wrap = this.querySelector('[data-gift-wrap-checkbox]');
      this.wrapValue = this.querySelector('[data-gift-wrap-value]');
      this.counter = this.querySelector('[data-gift-counter]');
      if (!this.checkbox || !this.giftValue || !this.message || !this.field || !this.status) return;
      this.form = document.getElementById(this.message.getAttribute('form'));
      store.initialise(this.checkbox.checked, this.message.value, Boolean(this.wrap?.checked));
      this.onInput = (event) => {
        clearTimeout(this.timer);
        if (event?.target === this.message) {
          // Typing: keep the draft now, save once the buyer pauses.
          store.draft(this.checkbox.checked, this.message.value, Boolean(this.wrap?.checked));
          this.timer = setTimeout(() => store.flush(), 400);
          return undefined;
        }
        return store.update(this.checkbox.checked, this.message.value, Boolean(this.wrap?.checked));
      };
      // Leaving the message box (for example to press Checkout) saves straight away.
      this.onChange = (event) => {
        if (event?.target !== this.message) return undefined;
        clearTimeout(this.timer);
        return store.flush();
      };
      this.onSubmit = async (event) => {
        if (this.submitting) return;
        clearTimeout(this.timer);
        if (!store.state.saving) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const submitter = event.submitter;
        await store.flush();
        // The form's own gift fields hold the latest details, so checkout carries on either way.
        this.submitting = true;
        try {
          if (submitter && typeof this.form.requestSubmit === 'function') {
            this.form.requestSubmit(submitter);
          } else {
            const checkout = document.createElement('input');
            checkout.type = 'hidden';
            checkout.name = 'checkout';
            this.form.appendChild(checkout);
            this.form.submit();
          }
        } finally {
          this.submitting = false;
        }
      };
      this.addEventListener('input', this.onInput);
      this.addEventListener('change', this.onChange);
      this.form?.addEventListener('submit', this.onSubmit, true);
      this.renderState();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.removeEventListener('input', this.onInput);
      this.removeEventListener('change', this.onChange);
      this.form?.removeEventListener('submit', this.onSubmit, true);
    }

    renderState() {
      if (!this.checkbox || !this.status) return;
      const { attributes, saving, error, saved } = store.state;
      const gift = attributes.Gift === 'Yes';
      this.checkbox.checked = gift;
      this.checkbox.setAttribute('aria-expanded', String(gift));
      this.giftValue.value = attributes.Gift;
      // Only touch the box when the wording differs, so the typing position never moves.
      if (this.message.value !== attributes['Gift message']) this.message.value = attributes['Gift message'];
      this.field.hidden = !gift;
      if (this.wrap) this.wrap.checked = attributes['Gift wrap'] === 'Yes';
      if (this.wrapValue) this.wrapValue.value = attributes['Gift wrap'];
      if (this.counter) this.counter.textContent = `${this.message.value.length}/200`;
      const text = error
        ? 'We couldn’t save your gift details. Please try again.'
        : gift && saved && !saving
          ? 'Gift details saved.'
          : '';
      if (this.status.textContent !== text) this.status.textContent = text;
      this.status.classList.toggle('rn-cart-gift__status--error', error);
      // Express checkout can't send the form's gift fields, so it waits for the save to finish.
      // 1 Nov (swarm fix batch 4, T4): and stays unavailable while the last save failed (it would carry the old gift details);
      // the next edit or tick retries the save. The ordinary Checkout button is not held: its form sends the gift fields itself.
      this.closest('.cart__footer')?.querySelectorAll('.additional-checkout-buttons').forEach((buttons) => {
        buttons.inert = saving || error;
        buttons.setAttribute('aria-busy', String(saving));
        buttons.classList.toggle('rn-express-held', error);
      });
    }
  }
  customElements.define('rn-cart-gift', RnCartGift);

  // 31 Oct (swarm fix 5): an emptied basket drops its gift details, so a returning buyer never finds an old gift note.
  window.FoxTheme?.pubsub?.subscribe(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
    const cart = event?.cart;
    if (!cart || cart.item_count !== 0) return;
    const attrs = cart.attributes || {};
    const remembered = store.state.attributes;
    if (attrs.Gift || attrs['Gift message'] || attrs['Gift wrap'] || remembered?.Gift || remembered?.['Gift message']) {
      store.update(false, '', false);
    }
    // Stock Hyper's gift-wrap note, in case it was ever used on this basket.
    if (attrs['gift-wrapping'] || attrs['gift-note']) {
      fetch(window.FoxTheme?.routes?.cart_update_url || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { 'gift-wrapping': '', 'gift-note': '' } })
      }).catch(() => {});
    }
  });
})();
